/**
 * HierarchyForm — authoring form for the `hierarchy` archetype.
 *
 * Produces:
 *   { base_record, recurse_via, projection: [field..],
 *     anchor_filter: [{field, operator, value}], depth_cap }
 *
 * Catalog-driven: hierarchy-capable records (with self_ref edges) from
 * GET /catalog/records?hierarchyCapable=true; fields (incl. code-owned lookup
 * pseudo-fields, with per-field operators) from
 * GET /catalog/records/:record/fields.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Icons } from '../../common/Icons';
import { reportCatalogService } from '../../../services/api';

const HierarchyForm = ({ spec, onChange, targetTenantId }) => {
  const [records, setRecords] = useState([]);
  const [fields, setFields] = useState([]);
  const [err, setErr] = useState(null);

  const record = records.find((r) => r.record_name === spec.base_record) || null;

  useEffect(() => {
    (async () => {
      try {
        const res = await reportCatalogService.getRecords(targetTenantId, true);
        setRecords(res.data || []);
      } catch (e) { setErr('Failed to load hierarchy-capable records.'); }
    })();
  }, [targetTenantId]);

  useEffect(() => {
    if (!spec.base_record) { setFields([]); return; }
    (async () => {
      try {
        const res = await reportCatalogService.getFields(targetTenantId, spec.base_record);
        setFields(res.data || []);
      } catch (e) { setErr('Failed to load fields.'); }
    })();
  }, [spec.base_record, targetTenantId]);

  const patch = useCallback((p) => onChange({ ...spec, ...p }), [spec, onChange]);

  const selectRecord = (base_record) => {
    onChange({ base_record, recurse_via: '', projection: [], anchor_filter: [], depth_cap: spec.depth_cap || 20 });
  };

  const filterableFields = fields.filter((f) => f.is_filterable);
  const fieldByName = (n) => fields.find((f) => f.field_name === n);

  const toggleProjection = (name) => {
    const cur = spec.projection || [];
    const next = cur.includes(name) ? cur.filter((c) => c !== name) : [...cur, name];
    patch({ projection: next });
  };

  const addAnchor = () => patch({ anchor_filter: [...(spec.anchor_filter || []), { field: '', operator: '', value: '' }] });
  const updateAnchor = (i, p) => {
    const next = [...(spec.anchor_filter || [])];
    next[i] = { ...next[i], ...p };
    patch({ anchor_filter: next });
  };
  const removeAnchor = (i) => patch({ anchor_filter: (spec.anchor_filter || []).filter((_, idx) => idx !== i) });

  const selectedProj = new Set(spec.projection || []);

  return (
    <div>
      {err && <div className="alert alert-warning mb-3"><Icons.AlertCircle size={18} /><span>{err}</span></div>}

      <Field label="Base record (must support a self-referential hierarchy)">
        <select className="form-control" value={spec.base_record || ''} onChange={(e) => selectRecord(e.target.value)}>
          <option value="">Select a record…</option>
          {records.map((r) => <option key={r.record_name} value={r.record_name}>{r.display_name}</option>)}
        </select>
      </Field>

      {record && (
        <>
          <Field label="Recurse via (which parent link to walk)">
            <select className="form-control" value={spec.recurse_via || ''} onChange={(e) => patch({ recurse_via: e.target.value })}>
              <option value="">Select an edge…</option>
              {(record.self_ref_edges || []).map((ed) => <option key={ed.edge_name} value={ed.edge_name}>{ed.label}</option>)}
            </select>
          </Field>

          {fields.length > 0 && (
            <Field label="Projection (columns to show; lookups marked)">
              <CheckGrid>
                {fields.map((f) => (
                  <CheckItem key={f.field_name} checked={selectedProj.has(f.field_name)} onChange={() => toggleProjection(f.field_name)}
                    label={f.display_name + (f.is_lookup ? ' (lookup)' : '')} />
                ))}
              </CheckGrid>
            </Field>
          )}

          <Field label="Anchor filter (which rows start the walk — the roots)">
            {(spec.anchor_filter || []).map((flt, i) => {
              const fld = fieldByName(flt.field);
              const ops = fld ? fld.operators : [];
              const op = ops.find((o) => o.value === flt.operator);
              const arity = op ? op.arity : 'one';
              return (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select className="form-control" style={{ width: '180px' }} value={flt.field}
                    onChange={(e) => updateAnchor(i, { field: e.target.value, operator: '', value: '' })}>
                    <option value="">Field…</option>
                    {filterableFields.map((f) => <option key={f.field_name} value={f.field_name}>{f.display_name}</option>)}
                  </select>
                  <select className="form-control" style={{ width: '160px' }} value={flt.operator}
                    onChange={(e) => updateAnchor(i, { operator: e.target.value })} disabled={!flt.field}>
                    <option value="">Operator…</option>
                    {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {arity === 'none' ? null : arity === 'two' ? (
                    <>
                      <input className="form-control" style={{ width: '120px' }} placeholder="from"
                        value={Array.isArray(flt.value) ? (flt.value[0] || '') : ''}
                        onChange={(e) => updateAnchor(i, { value: [e.target.value, Array.isArray(flt.value) ? (flt.value[1] || '') : ''] })} />
                      <input className="form-control" style={{ width: '120px' }} placeholder="to"
                        value={Array.isArray(flt.value) ? (flt.value[1] || '') : ''}
                        onChange={(e) => updateAnchor(i, { value: [Array.isArray(flt.value) ? (flt.value[0] || '') : '', e.target.value] })} />
                    </>
                  ) : arity === 'list' ? (
                    <input className="form-control" style={{ flex: 1, minWidth: '160px' }} placeholder="comma-separated values"
                      value={Array.isArray(flt.value) ? flt.value.join(',') : ''}
                      onChange={(e) => updateAnchor(i, { value: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })} />
                  ) : (
                    <input className="form-control" style={{ flex: 1, minWidth: '160px' }} placeholder="value"
                      value={flt.value ?? ''} onChange={(e) => updateAnchor(i, { value: e.target.value })} />
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => removeAnchor(i)} title="Remove"><Icons.X size={14} /></button>
                </div>
              );
            })}
            <button className="btn btn-secondary btn-sm" onClick={addAnchor}><Icons.Plus size={14} /> Add anchor filter</button>
          </Field>

          <Field label="Depth cap (generations below the root; 1 = direct children only)">
            <input type="number" min="1" max="50" className="form-control" style={{ width: '120px' }}
              value={spec.depth_cap || 20}
              onChange={(e) => patch({ depth_cap: parseInt(e.target.value, 10) || 1 })} />
          </Field>
        </>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    {label ? <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>{label}</label> : null}
    {children}
  </div>
);
const CheckGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px' }}>{children}</div>
);
const CheckItem = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
    <input type="checkbox" checked={checked} onChange={onChange} /><span>{label}</span>
  </label>
);

export default HierarchyForm;
